import { Fragment, useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from '@/components/ui/select';
import {
 Table,
 TableBody,
 TableCell,
 TableHead,
 TableHeader,
 TableRow,
} from '@/components/ui/table';
import {
 DropdownMenu,
 DropdownMenuContent,
 DropdownMenuItem,
 DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
 AlertDialog,
 AlertDialogAction,
 AlertDialogCancel,
 AlertDialogContent,
 AlertDialogDescription,
 AlertDialogFooter,
 AlertDialogHeader,
 AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Layers, Trash2, Plus, Calculator, MoreVertical, StickyNote } from 'lucide-react';
import { findMatchingLineItem } from './GarmentSubForm';
import { toast } from 'sonner';
import FabricCalculatorModal from '@/components/fabrics/FabricCalculatorModal';
import { FABRIC_ORDER_STATUSES, getFabricStatusConfig } from '@/constants/fabricStatuses';

const fabricTypeOptions = [
 { value: 'main_fabric', label: 'Main Fabric' },
 { value: 'lining', label: 'Lining' },
 { value: 'interlining', label: 'Interlining' },
 { value: 'contrast', label: 'Contrast' },
 { value: 'button', label: 'Button' },
 { value: 'thread', label: 'Thread' },
];

const priorityOptions = [
 { value: 'low', label: 'Low', color: 'bg-blue-600/80 text-white' },
 { value: 'medium', label: 'Medium', color: 'bg-amber-600/80 text-white' },
 { value: 'high', label: 'High', color: 'bg-orange-700/80 text-white' },
 { value: 'urgent', label: 'Urgent', color: 'bg-red-900/80 text-white' },
];

// Get user-friendly garment number (J1, T1, etc.)
const getGarmentDisplayNo = (garment, lineItemsWithRefs) => {
 if (!garment) return '—';
 
 // ALWAYS use garment_no as it's the permanent, stable identifier
 // Do NOT use line_item_ref as it can be regenerated on line item reordering
 if (garment.garment_no) {
 // Extract just the suffix (e.g., "PER-20260119001-J1" → "J1")
 const parts = garment.garment_no.split('-');
 return parts[parts.length - 1] || garment.garment_no;
 }
 
 // Fallback for legacy garments without garment_no
 return garment.garment_type?.substring(0, 2).toUpperCase() + '1' || '—';
};

// Use centralized status config
const getStatusConfig = getFabricStatusConfig;

const FabricOrderSubForm = ({ 
 fabricOrders, 
 garments, 
 lineItemsWithRefs, 
 suppliers,
 orderData,
 onAdd, 
 onDelete, 
 onStatusChange,
 onUpdate,
 onRefresh 
}) => {
 // Track all pending changes per fabric order
 const [pendingChanges, setPendingChanges] = useState({});
 // Mirrors `pendingChanges` synchronously so save calls scheduled via setTimeout(0)
 // (e.g. right after the fabric calculator applies a value) always read the LATEST
 // edit instead of a stale closure captured before that edit was committed to state.
 const pendingChangesRef = useRef({});
 
 // Delete confirmation dialog state
 const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
 const [fabricToDelete, setFabricToDelete] = useState(null);
 
 // Calculator modal state
 const [calculatorOpen, setCalculatorOpen] = useState(false);
 const [selectedFabricForCalculator, setSelectedFabricForCalculator] = useState(null);

 // Handle field change (track locally, don't save yet)
 // The ref is mutated synchronously (before setPendingChanges) so that any save
 // triggered right after - even in the same tick, without waiting for a re-render -
 // is guaranteed to see this latest edit instead of a stale pre-edit snapshot.
 const handleFieldChange = useCallback((fabricId, field, value) => {
 const next = {
 ...pendingChangesRef.current,
 [fabricId]: {
 ...(pendingChangesRef.current[fabricId] || {}),
 [field]: value
 }
 };
 pendingChangesRef.current = next;
 setPendingChanges(next);
 }, []);

 // Get current value (pending change or original)
 const getCurrentValue = (fo, field) => {
 if (pendingChanges[fo.fabric_id]?.[field] !== undefined) {
 return pendingChanges[fo.fabric_id][field];
 }
 return fo[field];
 };

 const saveFabricChanges = useCallback(async (fabricId) => {
 const changes = pendingChangesRef.current[fabricId];
 if (!changes || Object.keys(changes).length === 0) return true;

 try {
 if (changes.fabric_order_status) {
 await onStatusChange(fabricId, changes.fabric_order_status);
 }

 const otherChanges = { ...changes };
 delete otherChanges.fabric_order_status;
 if (Object.keys(otherChanges).length > 0) {
 await onUpdate(fabricId, otherChanges);
 }

 setPendingChanges(prev => {
 const next = {
 ...prev,
 [fabricId]: {},
 };
 return next;
 });
 pendingChangesRef.current = { ...pendingChangesRef.current, [fabricId]: {} };
 return true;
 } catch (error) {
 toast.error('Failed to auto-save fabric order changes');
 return false;
 }
}, [onStatusChange, onUpdate]);

 // Handle fabric calculator click
 const handleCalculator = (fo) => {
   // Enrich fabric order with customer info and customizations from line item
   const garment = garments?.find(g => g.garment_id === fo.garment_id);
   const lineItem = garment ? lineItemsWithRefs.find(li => li.line_item_id === garment.line_item_id) : null;
   
   const enrichedFabricOrder = {
     ...fo,
     customizations: lineItem?.customizations || [],
     customer_name: orderData?.customer_name || fo.customer_name,
     customer_no: orderData?.customer_no || fo.customer_no,
     customer_id: orderData?.customer_id || fo.customer_id,
   };
   
   console.log('=== EditOrderPage Calculator Debug ===');
   console.log('Fabric Order:', fo);
   console.log('Garment:', garment);
   console.log('Line Item:', lineItem);
   console.log('Enriched:', enrichedFabricOrder);
   
   setSelectedFabricForCalculator(enrichedFabricOrder);
   setCalculatorOpen(true);
 };
 
 // Handle calculator confirm
 const handleCalculatorConfirm = async (calculatedQty) => {
 if (!selectedFabricForCalculator) return;
 const fabricId = selectedFabricForCalculator.fabric_id;
 handleFieldChange(fabricId, 'quantity', calculatedQty);
 const saved = await saveFabricChanges(fabricId);
 if (saved) {
 toast.success(`Fabric quantity set to ${calculatedQty}m`);
 }
 };
 
 // Handle delete with confirmation
 const handleDeleteClick = (fabricId, fabricSku) => {
 setFabricToDelete({ fabricId, fabricSku });
 setDeleteConfirmOpen(true);
 };
 
 const handleConfirmDelete = async () => {
 if (!fabricToDelete) return;
 
 try {
 await onDelete(fabricToDelete.fabricId);
 toast.success('Fabric order deleted');
 } catch (error) {
 toast.error('Failed to delete fabric order');
 } finally {
 setDeleteConfirmOpen(false);
 setFabricToDelete(null);
 }
 };

 return (
 <div className="overflow-x-auto pb-24">
 {/* Header */}
 <div className="p-4 border-b border-slate-200 bg-white flex items-center justify-between">
 <p className="text-sm font-medium text-slate-700">
 {fabricOrders?.length || 0} fabric order(s)
 </p>
 <div className="flex items-center gap-2">
 <Button size="sm" onClick={onAdd}>
 <Plus className="w-4 h-4 mr-1" />
 Add Fabric Order
 </Button>
 </div>
 </div>
 
 <Table className="table-fixed min-w-[1120px]">
 <TableHeader>
 <TableRow className="bg-slate-50 border-b border-slate-200">
 <TableHead className="w-[72px] text-slate-700 font-semibold text-xs">Garment</TableHead>
 <TableHead className="w-[250px] text-slate-700 font-semibold text-xs">Fabric SKU</TableHead>
 <TableHead className="w-[120px] text-slate-700 font-semibold text-xs">Type</TableHead>
 <TableHead className="w-[100px] text-slate-700 font-semibold text-xs">Qty</TableHead>
 <TableHead className="w-[140px] text-slate-700 font-semibold text-xs">Supplier</TableHead>
 <TableHead className="w-[110px] text-slate-700 font-semibold text-xs">Priority</TableHead>
 <TableHead className="w-[140px] text-slate-700 font-semibold text-xs">Status</TableHead>
 <TableHead className="w-[80px] text-right text-slate-700 font-semibold text-xs">Actions</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {fabricOrders?.map((fo) => {
 const garment = garments?.find(g => g.garment_id === fo.garment_id);
 const displayNo = getGarmentDisplayNo(garment, lineItemsWithRefs);
 const hasChanges = pendingChanges[fo.fabric_id] && Object.keys(pendingChanges[fo.fabric_id]).length > 0;
 const statusConfig = getStatusConfig(getCurrentValue(fo, 'fabric_order_status') || 'not_ordered');
 
return (
<Fragment key={fo.fabric_id}>
<TableRow className={`hover:bg-slate-50 border-b-0 ${hasChanges ? 'bg-orange-50/50' : 'bg-white'}`}>
{/* Garment No */}
<TableCell className="pr-1">
<Badge variant="outline" className="font-mono text-xs font-semibold bg-orange-50 text-orange-700 border-orange-300">
{displayNo}
</Badge>
</TableCell>

{/* Fabric SKU - Inline editable */}
<TableCell className="pl-1">
<Input
value={getCurrentValue(fo, 'fabric_sku') || ''}
onChange={(e) => handleFieldChange(fo.fabric_id, 'fabric_sku', e.target.value)}
onBlur={() => saveFabricChanges(fo.fabric_id)}
placeholder="Enter SKU..."
className="h-8 w-full min-w-[220px] text-xs text-slate-700 bg-white border-dashed"
/>
</TableCell>

{/* Fabric Type - Inline editable */}
<TableCell>
<Select
value={getCurrentValue(fo, 'fabric_type') || 'main_fabric'}
onValueChange={(v) => {
handleFieldChange(fo.fabric_id, 'fabric_type', v);
setTimeout(() => saveFabricChanges(fo.fabric_id), 0);
}}
>
<SelectTrigger className="h-8 text-xs border-dashed">
<SelectValue />
</SelectTrigger>
<SelectContent>
{fabricTypeOptions.map(opt => (
<SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
))}
</SelectContent>
</Select>
</TableCell>

{/* Quantity - Inline editable with calculator */}
<TableCell>
<div className="flex items-center gap-1">
<Input
type="number"
value={getCurrentValue(fo, 'quantity') ?? ''}
onChange={(e) => handleFieldChange(fo.fabric_id, 'quantity', e.target.value ? parseFloat(e.target.value) : null)}
onBlur={() => saveFabricChanges(fo.fabric_id)}
placeholder="—"
className="h-8 text-xs text-slate-700 w-16 bg-white border-dashed"
step="0.1"
min="0"
/>
<Button
variant="ghost"
size="icon"
className="h-7 w-7 text-slate-500 hover:text-orange-600"
onClick={() => handleCalculator(fo)}
title="Fabric calculator"
>
<Calculator className="w-4 h-4" />
</Button>
</div>
</TableCell>

{/* Supplier - Inline editable dropdown */}
<TableCell>
<Select
value={getCurrentValue(fo, 'supplier_id') || '__none__'}
onValueChange={(v) => {
handleFieldChange(fo.fabric_id, 'supplier_id', v === '__none__' ? null : v);
setTimeout(() => saveFabricChanges(fo.fabric_id), 0);
}}
>
<SelectTrigger className="h-8 text-xs border-dashed">
<SelectValue placeholder="Select..." />
</SelectTrigger>
<SelectContent>
<SelectItem value="__none__">— Select —</SelectItem>
{suppliers?.map(s => (
<SelectItem key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</SelectItem>
))}
</SelectContent>
</Select>
</TableCell>

{/* Priority - Inline editable dropdown */}
<TableCell>
{(() => {
const currentPriority = getCurrentValue(fo, 'priority') || 'medium';
const priorityConfig = priorityOptions.find(p => p.value === currentPriority) || priorityOptions[1];
return (
<Select
value={currentPriority}
onValueChange={(v) => {
handleFieldChange(fo.fabric_id, 'priority', v);
setTimeout(() => saveFabricChanges(fo.fabric_id), 0);
}}
>
<SelectTrigger className="h-8 text-xs border-0 bg-transparent hover:bg-secondary/50 p-0">
<Badge variant="outline" className={`${priorityConfig.color} border-0 text-xs`}>
{priorityConfig.label}
</Badge>
</SelectTrigger>
<SelectContent>
{priorityOptions.map(opt => (
<SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
))}
</SelectContent>
</Select>
);
})()}
</TableCell>

{/* Status - Inline editable dropdown matching FabricProcurementPage */}
<TableCell>
<Select
value={getCurrentValue(fo, 'fabric_order_status') || 'not_ordered'}
onValueChange={(v) => {
handleFieldChange(fo.fabric_id, 'fabric_order_status', v);
setTimeout(() => saveFabricChanges(fo.fabric_id), 0);
}}
>
<SelectTrigger className="h-8 text-xs border-0 bg-transparent hover:bg-secondary/50 p-0">
<Badge variant="outline" className={`${statusConfig.color} text-xs`}>
{statusConfig.label}
</Badge>
</SelectTrigger>
<SelectContent>
{FABRIC_ORDER_STATUSES.map(opt => (
<SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
))}
</SelectContent>
</Select>
</TableCell>

{/* Actions - 3-Dot Menu for Delete */}
<TableCell className="text-right">
<DropdownMenu>
<DropdownMenuTrigger asChild>
<Button
variant="ghost"
size="icon"
className="h-8 w-8 text-slate-500 hover:text-slate-700"
>
<MoreVertical className="w-4 h-4" />
</Button>
</DropdownMenuTrigger>
<DropdownMenuContent align="end">
<DropdownMenuItem
className="text-red-600 focus:text-red-600 cursor-pointer"
onClick={() => handleDeleteClick(fo.fabric_id, getCurrentValue(fo, 'fabric_sku') || 'this fabric order')}
>
<Trash2 className="w-4 h-4 mr-2" />
Delete Fabric Order
</DropdownMenuItem>
</DropdownMenuContent>
</DropdownMenu>
</TableCell>
</TableRow>

{/* Notes Row - Similar to garment subtable */}
<TableRow className={`border-b ${hasChanges ? 'bg-orange-50/50' : 'bg-slate-50/50'}`}>
<TableCell colSpan={8} className="py-1.5 px-4">
<div className="flex items-center gap-2">
<StickyNote className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
<Input
value={getCurrentValue(fo, 'notes') || ''}
onChange={(e) => handleFieldChange(fo.fabric_id, 'notes', e.target.value)}
onBlur={() => saveFabricChanges(fo.fabric_id)}
placeholder="Add notes..."
className="h-7 text-xs text-slate-700 bg-white border-dashed"
/>
</div>
</TableCell>
</TableRow>
</Fragment>
);
 })}
 </TableBody>
 </Table>
 
 {!fabricOrders?.length && (
 <div className="p-8 text-center text-slate-500">
 <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
 <p>No fabric orders</p>
 <p className="text-sm">Add fabric requirements for garments</p>
 </div>
 )}
 
 {/* Delete Confirmation Dialog */}
 <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
 <AlertDialogContent>
 <AlertDialogHeader>
 <AlertDialogTitle>Delete Fabric Order</AlertDialogTitle>
 <AlertDialogDescription>
 Are you sure you want to delete <strong>{fabricToDelete?.fabricSku}</strong>? This action cannot be undone.
 </AlertDialogDescription>
 </AlertDialogHeader>
 <AlertDialogFooter>
 <AlertDialogCancel>Cancel</AlertDialogCancel>
 <AlertDialogAction 
 onClick={handleConfirmDelete}
 className="bg-red-600 hover:bg-red-700"
 >
 Delete
 </AlertDialogAction>
 </AlertDialogFooter>
 </AlertDialogContent>
 </AlertDialog>
 
 {/* Fabric Calculator Modal */}
 <FabricCalculatorModal
   open={calculatorOpen}
   onOpenChange={setCalculatorOpen}
   fabricOrder={selectedFabricForCalculator}
   onConfirm={handleCalculatorConfirm}
 />
 </div>
 );
};

export default FabricOrderSubForm;
